import type { ErrorCopyFor } from "@amtana/browser";
import { phrase } from "@amtana/i18n/catalog";
import type { Catalogs, Translation } from "@amtana/i18n/catalog";
import type { ORGANIZATION_ERROR_CODES } from "@amtana/organization-contract";

// What a person reads when the organization or a member change refuses
// (packages/module ADR-001), in English and Spanish, read in the active
// language when the error shows.

const EN = {
  notFoundTitle: "This organization no longer exists",
  notFoundDescription: "It may have been removed. Reload the page to see where you stand.",
  slugUnavailableTitle: "Could not find a free address for this organization",
  slugUnavailableDescription: "Try a different name.",
  membershipNotFoundTitle: "This person is no longer a member",
  membershipNotFoundDescription: "Reload the list to see who belongs now.",
  lastAdminTitle: "An organization always needs at least one admin",
  lastAdminDescription: "Make someone else admin first, then try again.",
  selfPromotionRefusedTitle: "You can't give yourself a higher role",
  selfPromotionRefusedDescription: "Ask another admin to change it for you.",
  onlyOrganizationTitle: "This is your only organization",
  onlyOrganizationDescription: "You need to belong to at least one, so you can't leave this one.",
  invitationNotFoundTitle: "This invite link is no longer valid",
  invitationNotFoundDescription: "It may have been revoked, declined or already used.",
  invitationExpiredTitle: "This invite has expired",
  invitationExpiredDescription: "Ask an admin to resend it.",
  invitationWrongAddressTitle: "This invite was sent to a different address",
  invitationWrongAddressDescription: "Sign in with the address it was sent to, then open the link again.",
  invitationEmailUnverifiedTitle: "Verify your email first",
  invitationEmailUnverifiedDescription: "Confirm your address, then open the invite link again.",
  invitationAlreadyMemberTitle: "Already a member",
  invitationAlreadyMemberDescription: "This person already belongs to the organization.",
  invitationResendRateLimitedTitle: "Wait a few minutes before resending",
  invitationResendRateLimitedDescription: "This invite was just sent. Try again shortly.",
  logoInvalidTitle: "That file could not be read as an image",
  logoInvalidDescription: "Choose a different file.",
  logoUnsupportedTypeTitle: "That image type isn't supported",
  logoUnsupportedTypeDescription: "Choose a PNG, JPEG or WebP image.",
  logoTooLargeTitle: "That image is too large",
  logoTooLargeDescription: "Choose a smaller file.",
  deletionAlreadyPendingTitle: "This organization is already pending deletion",
  deletionAlreadyPendingDescription: "Reload the page to see when it will be purged.",
  notPendingDeletionTitle: "This organization is not pending deletion",
  notPendingDeletionDescription: "There is nothing to restore.",
  restoreRefusedTitle: "Only an admin can restore this organization",
  restoreRefusedDescription: "Ask an admin of this organization to restore it.",
} as const;

const ES: Translation<typeof EN> = {
  notFoundTitle: "Esta organización ya no existe",
  notFoundDescription: "Puede que se haya eliminado. Recarga la página para ver cómo estás.",
  slugUnavailableTitle: "No se encontró una dirección libre para esta organización",
  slugUnavailableDescription: "Prueba con otro nombre.",
  membershipNotFoundTitle: "Esta persona ya no es miembro",
  membershipNotFoundDescription: "Recarga la lista para ver quién pertenece ahora.",
  lastAdminTitle: "Una organización siempre necesita al menos un administrador",
  lastAdminDescription: "Nombra primero a otro administrador y vuelve a intentarlo.",
  selfPromotionRefusedTitle: "No puedes darte un rol más alto",
  selfPromotionRefusedDescription: "Pide a otro administrador que lo cambie por ti.",
  onlyOrganizationTitle: "Esta es tu única organización",
  onlyOrganizationDescription: "Necesitas pertenecer al menos a una, así que no puedes salir de esta.",
  invitationNotFoundTitle: "Este enlace de invitación ya no es válido",
  invitationNotFoundDescription: "Puede que se haya revocado, rechazado o usado.",
  invitationExpiredTitle: "Esta invitación venció",
  invitationExpiredDescription: "Pide a un administrador que la reenvíe.",
  invitationWrongAddressTitle: "Esta invitación se envió a otra dirección",
  invitationWrongAddressDescription:
    "Inicia sesión con la dirección a la que se envió y abre el enlace de nuevo.",
  invitationEmailUnverifiedTitle: "Verifica primero tu correo",
  invitationEmailUnverifiedDescription: "Confirma tu dirección y abre el enlace de invitación de nuevo.",
  invitationAlreadyMemberTitle: "Ya es miembro",
  invitationAlreadyMemberDescription: "Esta persona ya pertenece a la organización.",
  invitationResendRateLimitedTitle: "Espera unos minutos antes de reenviar",
  invitationResendRateLimitedDescription:
    "Esta invitación se acaba de enviar. Inténtalo de nuevo en un rato.",
  logoInvalidTitle: "No se pudo leer ese archivo como imagen",
  logoInvalidDescription: "Elige otro archivo.",
  logoUnsupportedTypeTitle: "Ese tipo de imagen no es compatible",
  logoUnsupportedTypeDescription: "Elige una imagen PNG, JPEG o WebP.",
  logoTooLargeTitle: "Esa imagen es demasiado grande",
  logoTooLargeDescription: "Elige un archivo más pequeño.",
  deletionAlreadyPendingTitle: "Esta organización ya tiene una eliminación pendiente",
  deletionAlreadyPendingDescription: "Recarga la página para ver cuándo se eliminará para siempre.",
  notPendingDeletionTitle: "Esta organización no tiene una eliminación pendiente",
  notPendingDeletionDescription: "No hay nada que restaurar.",
  restoreRefusedTitle: "Solo un administrador puede restaurar esta organización",
  restoreRefusedDescription: "Pide a un administrador de esta organización que la restaure.",
};

export const ORGANIZATION_ERROR_CATALOGS: Catalogs<typeof EN> = { en: EN, es: ES };

export const ORGANIZATION_ERROR_COPY = {
  organization_not_found: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "notFoundTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "notFoundDescription"),
  },
  organization_slug_unavailable: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "slugUnavailableTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "slugUnavailableDescription"),
  },
  organization_membership_not_found: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "membershipNotFoundTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "membershipNotFoundDescription"),
  },
  organization_last_admin: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "lastAdminTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "lastAdminDescription"),
  },
  organization_self_promotion_refused: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "selfPromotionRefusedTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "selfPromotionRefusedDescription"),
  },
  organization_only_organization: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "onlyOrganizationTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "onlyOrganizationDescription"),
  },
  organization_invitation_not_found: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "invitationNotFoundTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "invitationNotFoundDescription"),
  },
  organization_invitation_expired: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "invitationExpiredTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "invitationExpiredDescription"),
  },
  organization_invitation_wrong_address: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "invitationWrongAddressTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "invitationWrongAddressDescription"),
  },
  organization_invitation_email_unverified: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "invitationEmailUnverifiedTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "invitationEmailUnverifiedDescription"),
  },
  organization_invitation_already_member: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "invitationAlreadyMemberTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "invitationAlreadyMemberDescription"),
  },
  organization_invitation_resend_rate_limited: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "invitationResendRateLimitedTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "invitationResendRateLimitedDescription"),
  },
  organization_logo_invalid: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "logoInvalidTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "logoInvalidDescription"),
  },
  organization_logo_unsupported_type: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "logoUnsupportedTypeTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "logoUnsupportedTypeDescription"),
  },
  organization_logo_too_large: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "logoTooLargeTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "logoTooLargeDescription"),
  },
  organization_deletion_already_pending: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "deletionAlreadyPendingTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "deletionAlreadyPendingDescription"),
  },
  organization_not_pending_deletion: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "notPendingDeletionTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "notPendingDeletionDescription"),
  },
  organization_restore_refused: {
    title: phrase(ORGANIZATION_ERROR_CATALOGS, "restoreRefusedTitle"),
    description: phrase(ORGANIZATION_ERROR_CATALOGS, "restoreRefusedDescription"),
  },
} satisfies ErrorCopyFor<typeof ORGANIZATION_ERROR_CODES>;
